"use client";

import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, Wind, Shield, RefreshCw } from 'lucide-react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';
export default function TheScienceOfPositivePressure() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const steps = [{
    label: "Inflow",
    icon: <Wind className="size-4" />,
    description: "Ambient air is gently drawn into the system, passing through a preliminary barrier that captures large particulates and dust.",
    badgeVariant: "outline" as const
  }, {
    label: "Filtration",
    icon: <Shield className="size-4" />,
    description: "Micro-pollutants, allergens, and PM2.5 are trapped within the Clean Air Bubble core, ensuring only clinically pure air enters your sanctuary.",
    badgeVariant: "default" as const
  }, {
    label: "Outflow",
    icon: <RefreshCw className="size-4" />,
    description: "Continuous positive pressure forces stale air and CO2 out through natural gaps, creating an invisible shield that prevents external contaminants from entering.",
    badgeVariant: "secondary" as const
  }];
  return <section id="the-science-of-positive-pressure" className="relative w-full bg-background py-24 lg:py-40 overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          
          {/* Visualization */}
          <div className={`relative w-full h-[400px] lg:h-[600px] rounded-2xl overflow-hidden bg-secondary/20 border border-border/50 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 flex items-center justify-center">
              {/* CSS-based animation replacing p5.js */}
              <div className="relative w-full h-full">
                {/* Central point with pulsing ripples */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  {/* Core center point */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-5 rounded-full bg-white shadow-lg z-10" />
                  
                  {/* Animated ripples */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-20 rounded-full border-2 border-blue-400/50 animate-[ping_3s_ease-in-out_infinite]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-32 rounded-full border-2 border-blue-400/30 animate-[ping_3s_ease-in-out_infinite_0.5s]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-48 rounded-full border-2 border-blue-400/20 animate-[ping_3s_ease-in-out_infinite_1s]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-64 rounded-full border-2 border-blue-400/10 animate-[ping_3s_ease-in-out_infinite_1.5s]" />
                  
                  {/* Static glow rings */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-16 rounded-full bg-white/5" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-20 rounded-full bg-white/5" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-24 rounded-full bg-white/5" />
                </div>

                {/* Floating particles (using pure CSS) */}
                {[...Array(20)].map((_, i) => <div key={i} className="absolute size-1 rounded-full bg-gray-400/60" style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${3 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 2}s`,
                animationDirection: i % 2 === 0 ? 'normal' : 'reverse'
              }} />)}
              </div>
            </div>
            
            {/* Labels */}
            <div className="absolute top-6 left-6">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Fig 2.1 — Positive Pressure Dynamics</span>
            </div>
            <div className="absolute bottom-6 right-6 flex gap-4">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-blue-400/50"></span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Clean Air</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-gray-400/50"></span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Pollutants</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`flex flex-col gap-10 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-foreground">
                The Physics of Purity
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Our technology doesn't just clean air; it restructures your environment. By maintaining a higher internal pressure, we turn your home into an exclusion zone for pollution.
              </p>
            </div>

            <div className="space-y-8">
              {steps.map((step, i) => <div key={i} data-index={i} className="group relative pl-8 border-l border-border/50 hover:border-primary/50 transition-colors duration-500">
                  <div className="absolute -left-[13px] top-0 bg-background p-1">
                    <div className={`size-4 rounded-full border-2 transition-colors duration-500 ${i === 1 ? 'border-primary bg-primary' : 'border-muted-foreground/30 group-hover:border-primary'}`} />
                  </div>
                  
                  <div className="flex flex-col gap-3">
                    <Badge variant={step.badgeVariant} className="w-fit px-3 py-1 text-sm gap-2">
                      {step.icon}
                      {step.label}
                    </Badge>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild variant="link" className="px-0 text-primary group-hover:text-primary/80">
                <Link to="/TheSciencePage">
                  Explore the full schematic <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
            opacity: 0.3;
          }
          25% {
            transform: translate(-20px, -30px);
            opacity: 0.6;
          }
          50% {
            transform: translate(0, -50px);
            opacity: 0.8;
          }
          75% {
            transform: translate(20px, -30px);
            opacity: 0.6;
          }
        }
      `}</style>
    </section>;
}